import { VerificationStatus } from "@/domains/integrations/verification-status";
import {
  MDSTextField,
  MDSButton,
  MDSButtonVariant,
  MDSTextFieldVariant,
  MDSTextWeight,
  MDSTextVariant,
} from "@/mds";
import { IntegrationsContentResend } from "@/pages/integrations/shared/integrations-resend";
import { StyledSubTitle } from "@/pages/integrations/shared/integrations.styles";
import { useAppStore } from "@/store";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import { AppStoreEmailSendersStore } from "@/store/email-senders/AppStoreEmailSendersStore";
import { TextMessageSenderObservable } from "@/store/text-message-senders/TextMessageSenderObservable";
import { AppStoreTextMessageSendersStore } from "@/store/text-message-senders/AppStoreTextMessageSendersStore";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTimeout } from "usehooks-ts";

type TProps = {
  unVerifiedItem: EmailSenderObservable | TextMessageSenderObservable;
  hasItems: boolean;
};

export const IntegrationsPageContentForm = observer<TProps>(({ unVerifiedItem, hasItems }) => {
  const { store } = useAppStore();
  const integrationStore =
    unVerifiedItem instanceof EmailSenderObservable ? store.emailSenders : store.textMessageSenders;

  const {
    isCreating,
    create,
    isVerifying,
    verify,
    isRequestingNewVerificationCode,
    clearUnverifiedItem,
  } = integrationStore;

  const {
    id,
    input,
    isInputValid,
    setInput,
    setVerificationToken,
    verificationStatus,
    verificationToken,
  } = unVerifiedItem;

  const isEmail = unVerifiedItem instanceof EmailSenderObservable;
  const label = isEmail ? "email" : "phone number";
  const awaitVerification = verificationStatus === VerificationStatus.AwaitsVerification;
  const isinValidVerificationToken = verificationStatus === VerificationStatus.Invalid;
  const isOnVerificationStep =
    verificationStatus === VerificationStatus.AwaitsVerification ||
    verificationStatus === VerificationStatus.Invalid;

  const timeout = isEmail
    ? AppStoreEmailSendersStore.VERIFICATION_TIMEOUT
    : AppStoreTextMessageSendersStore.VERIFICATION_TIMEOUT;

  const { isVerificationTimedOut, setIsVerificationTimedOut } = useVerificationTimeout({
    awaitVerification,
    timeout,
  });

  const handleInputChange = (value: string) => {
    setInput(value);
  };

  const handleVerificationTokenChange = (value: string) => {
    setIsVerificationTimedOut(false);
    setVerificationToken(value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!isOnVerificationStep) {
      create(input);
      return;
    }

    if (!verificationToken) {
      return;
    }

    verify(id, verificationToken);
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StyledSection>
        <StyledSubTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
          Connect {hasItems ? "another " : ""}
          {label}
        </StyledSubTitle>
        <ExpandableContent>
          {isEmail ? (
            <MDSTextField
              variant={MDSTextFieldVariant.Default}
              type="email"
              value={input}
              placeholder={`Enter ${label}`}
              isDisabled={isCreating}
              onChange={handleInputChange}
              isReadOnly={awaitVerification}
              isInvalid={false}
              aria-label={label}
              autoFocus
            />
          ) : (
            <MDSTextField
              variant={MDSTextFieldVariant.Default}
              type="tel"
              value={input}
              placeholder={`Enter ${label}`}
              pattern="[0-9]*"
              isDisabled={isCreating}
              onChange={handleInputChange}
              isReadOnly={awaitVerification}
              isInvalid={false}
              aria-label={label}
              autoFocus
            />
          )}
        </ExpandableContent>
        {isOnVerificationStep && (
          <MDSTextField
            variant={MDSTextFieldVariant.Form}
            isDisabled={isCreating}
            value={verificationToken}
            placeholder="Enter verification code"
            onChange={handleVerificationTokenChange}
            isInvalid={isinValidVerificationToken}
            aria-label="Verification code"
            autoComplete="one-time-code"
            autoFocus
          />
        )}

        <FooterStyles>
          <LeftStyles>
            {isinValidVerificationToken && (
              <ErrorStyles>
                Incorrect verification code.{" "}
                <IntegrationsContentResend integrationStore={integrationStore} isError />
              </ErrorStyles>
            )}
            {isVerificationTimedOut && (
              <TimeoutStyles>
                Didn’t receive {label === "email" ? "an email" : "a text"}?{" "}
                <IntegrationsContentResend integrationStore={integrationStore} />
              </TimeoutStyles>
            )}
          </LeftStyles>
          <RightStyles>
            <MDSButton
              type="reset"
              isDisabled={isCreating || isVerifying}
              onClick={clearUnverifiedItem}
            >
              Cancel
            </MDSButton>
            {isOnVerificationStep ? (
              <MDSButton
                type="submit"
                isLoading={isVerifying || isRequestingNewVerificationCode}
                variant={MDSButtonVariant.Contrast}
                isDisabled={!verificationToken || isinValidVerificationToken}
              >
                Verify
              </MDSButton>
            ) : (
              <MDSButton
                type="submit"
                isLoading={isCreating}
                isDisabled={!isInputValid}
                variant={MDSButtonVariant.Contrast}
                onClick={handleSubmit}
              >
                Get verification code
              </MDSButton>
            )}
          </RightStyles>
        </FooterStyles>
      </StyledSection>
    </StyledForm>
  );
});

const useVerificationTimeout = ({
  awaitVerification,
  timeout,
}: {
  awaitVerification: boolean;
  timeout: number;
}) => {
  const [isVerificationTimedOut, setIsVerificationTimedOut] = useState(false);

  useTimeout(
    () => {
      setIsVerificationTimedOut(true);
    },
    awaitVerification ? timeout : null
  );

  return { isVerificationTimedOut, setIsVerificationTimedOut };
};

export const StyledForm = styled.form(() => ({
  width: "100%",
}));

export const StyledSection = styled.div`
  display: flex;
  max-width: 784px;
  padding: ${({ theme }) => theme.spacing[5]};
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.surface.pane};
  gap: ${({ theme }) => theme.spacing[4]};
`;

export const ExpandableContent = styled.div`
  flex: 1;
  width: 100%;
`;

const FooterStyles = styled.div({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const LeftStyles = styled.div({
  display: "flex",
  gap: "8px",
});

const RightStyles = styled.div({
  display: "flex",
  gap: "8px",
});

const ErrorStyles = styled.div(({ theme }) => ({
  color: theme.colors.red.x500,
  fontSize: theme.fontSizes.xsmall,
  lineHeight: theme.lineHeights.xsmall,
}));

const TimeoutStyles = styled.div(({ theme }) => ({
  color: theme.colors.grey.x500,
  fontSize: theme.fontSizes.xsmall,
  lineHeight: theme.lineHeights.xsmall,
}));
